import { useQuery } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router';
import {
  EPSS_MULTIPLIER,
  EPSS_THRESHOLD,
  KEV_MULTIPLIER,
  REACH_WEIGHT,
  SEVERITY_WEIGHT,
  type Exposure,
  type ExposureMonth,
  type ExposureWeakness,
  type Reach,
} from '@ai-secure-twin/shared';
import { Icon } from '../../components/Icon';
import { api, ApiError } from '../../lib/api';
import { NODE_TYPE } from '../../lib/cloud-graph';

const fmt = (n: number) => n.toLocaleString(undefined, { maximumFractionDigits: 1 });

const WEAKNESS: Record<ExposureWeakness, string> = {
  VULNERABLE_COMPONENT: 'Vulnerable components',
  SECURITY_HEADERS: 'Missing security headers',
  CROSS_ORIGIN: 'Cross-origin misconfiguration',
  INFORMATION_DISCLOSURE: 'Information disclosure',
  OTHER: 'Other weaknesses',
};

const TABS = [
  ['overview', 'Overview'],
  ['paths', 'Proof paths'],
  ['reduction', 'Debt reduction'],
  ['applications', 'By application'],
] as const;
type Tab = (typeof TABS)[number][0];
const isTab = (t: string | null): t is Tab => TABS.some(([key]) => key === t);

const REACH: Record<Reach, { label: string; cls: string }> = {
  SECRETS: { label: 'Secrets', cls: 'reach-secrets' },
  DATA: { label: 'Data', cls: 'reach-data' },
  COMPUTE: { label: 'Compute', cls: 'reach-compute' },
  EDGE_ONLY: { label: 'Edge', cls: 'reach-edge' },
  NOT_IN_ACCOUNT: { label: 'Not traced', cls: 'reach-none' },
};

/**
 * Exposure (ADR 0010) — the console's landing, and CTEM stage 3.
 *
 * One figure for the proved exposure that is open, how it moved, what it is made of, and the
 * single changes that pay most of it down. Every number comes from `/v1/exposure`, which no
 * model touches; the weights are shown beside the formula so any figure can be recomputed.
 */
export function ExposurePage() {
  const exposure = useQuery({
    queryKey: ['exposure'],
    queryFn: api.getExposure,
    refetchInterval: 120_000,
  });

  return (
    <div className="page">
      {exposure.isLoading ? (
        <p className="muted">Weighing every open finding against what it reaches…</p>
      ) : exposure.isError || !exposure.data ? (
        <p className="login-error" role="alert">
          {exposure.error instanceof ApiError ? exposure.error.message : 'Could not load exposure.'}
        </p>
      ) : (
        <ExposureView data={exposure.data} />
      )}
    </div>
  );
}

export function ExposureView({ data }: { data: Exposure }) {
  const [params, setParams] = useSearchParams();
  const requested = params.get('tab');
  const tab: Tab = isTab(requested) ? requested : 'overview';
  const delta =
    data.previousIndex !== null && data.previousIndex > 0
      ? ((data.index - data.previousIndex) / data.previousIndex) * 100
      : null;
  const totalWeakness = Math.max(1, ...data.byWeakness.map((w) => w.debt));
  const totalFix = Math.max(1, ...data.fixes.map((f) => f.debt));
  const totalCut = Math.max(1, ...data.cuts.map((c) => c.debtReduction));
  const totalApp = Math.max(1, ...data.byApplication.map((a) => a.debt));

  return (
    <div className="xp">
      <section className="card xp-frame" aria-labelledby="xp-title">
        <header className="xp-frame-head">
          <span className="xp-badge" aria-hidden="true">
            <Icon name="shield" size={20} />
          </span>
          <div>
            <h1 id="xp-title">Exposure Debt</h1>
            <p className="muted">{acrossApplications(data.byApplication)}</p>
          </div>
        </header>
        <div className="pp-tabs" role="tablist" aria-label="Exposure views">
          {TABS.map(([key, label]) => (
            <button
              key={key}
              type="button"
              role="tab"
              aria-selected={tab === key}
              className={`pp-tab${tab === key ? ' is-on' : ''}`}
              onClick={() =>
                setParams(
                  (p) => {
                    if (key === 'overview') p.delete('tab');
                    else p.set('tab', key);
                    return p;
                  },
                  { replace: true },
                )
              }
            >
              {label}
            </button>
          ))}
        </div>

        <div
          className="xp-frame-body"
          role="tabpanel"
          aria-label={TABS.find(([k]) => k === tab)![1]}
        >
          {tab === 'overview' ? (
            <>
              <div className="xp-grid xp-2-3">
                <section className="card panel xp-index" aria-labelledby="xp-index">
                  <span id="xp-index" className="xp-eyebrow">
                    Exposure debt index
                  </span>
                  <div className="xp-big-row">
                    <span className="xp-big">{fmt(data.index)}</span>
                    {delta !== null ? (
                      <span className={delta <= 0 ? 'xp-delta-down' : 'xp-delta-up'}>
                        {delta <= 0 ? '▼' : '▲'} {fmt(Math.abs(delta))}% vs{' '}
                        {fmt(data.previousIndex!)} last month
                      </span>
                    ) : null}
                  </div>
                  <div className="xp-kpis">
                    <div>
                      <span className="xp-kpi-n">{data.loop.prioritize.openFindings}</span>
                      <span className="xp-kpi-l">Open findings weighed</span>
                    </div>
                    <div>
                      <span className="xp-kpi-n">{data.loop.validate.reachSensitive}</span>
                      <span className="xp-kpi-l">Reach data or a secret</span>
                    </div>
                    <div>
                      <span className="xp-kpi-n xp-verified">{data.loop.mobilize.fixesProved}</span>
                      <span className="xp-kpi-l">Fixes proved by re-scan</span>
                    </div>
                  </div>
                  {!data.threatIntel ? (
                    <p className="field-hint">
                      The knowledge base was not reachable, so threat is severity only — no KEV or
                      EPSS.
                    </p>
                  ) : null}
                </section>

                <section className="card panel" aria-labelledby="xp-trend">
                  <div className="xp-head">
                    <span id="xp-trend" className="xp-eyebrow">
                      Index trend · 6 months
                    </span>
                    {delta !== null ? (
                      <span className={delta <= 0 ? 'xp-delta-down' : 'xp-delta-up'}>
                        {delta <= 0 ? '↘ Reducing' : '↗ Growing'}
                      </span>
                    ) : null}
                  </div>
                  <div className="xp-legend">
                    <span>
                      <i className="sw sw-index" />
                      Debt index
                    </span>
                    <span>
                      <i className="sw sw-new" />
                      New findings
                    </span>
                    <span>
                      <i className="sw sw-closed" />
                      Closed as fixed
                    </span>
                  </div>
                  <TrendChart trend={data.trend} />
                  <p className="field-hint">
                    Reconstructed from each finding&rsquo;s first-seen and triage dates, with
                    today&rsquo;s reach.
                  </p>
                </section>
              </div>

              <section className="card panel" aria-labelledby="xp-formula">
                <h2 id="xp-formula" className="panel-title">
                  How the index is calculated
                </h2>
                <p className="muted xp-lede">
                  Every open finding contributes. Its weight is the product of three factors, each
                  read from evidence Guardian already holds — none from a model.
                </p>
                <div className="xp-factors">
                  <div className="xp-factor">
                    <b>
                      <span className="xp-dot xp-dot-r">R</span>Reach
                    </b>
                    <p>
                      What its proof path ends at: secret {REACH_WEIGHT.SECRETS}, data store{' '}
                      {REACH_WEIGHT.DATA}, compute {REACH_WEIGHT.COMPUTE}, edge{' '}
                      {REACH_WEIGHT.EDGE_ONLY}, not traced {REACH_WEIGHT.NOT_IN_ACCOUNT}.
                    </p>
                  </div>
                  <div className="xp-factor">
                    <b>
                      <span className="xp-dot xp-dot-t">T</span>Threat
                    </b>
                    <p>
                      Severity: critical {SEVERITY_WEIGHT.CRITICAL}, high {SEVERITY_WEIGHT.HIGH},
                      medium {SEVERITY_WEIGHT.MEDIUM}, low {SEVERITY_WEIGHT.LOW}, info{' '}
                      {SEVERITY_WEIGHT.INFORMATIONAL}; ×{KEV_MULTIPLIER} in CISA KEV, ×
                      {EPSS_MULTIPLIER} at EPSS ≥ {EPSS_THRESHOLD}.
                    </p>
                  </div>
                  <div className="xp-factor">
                    <b>
                      <span className="xp-dot xp-dot-g">G</span>Residual gap
                    </b>
                    <p>
                      Open 1, accepted risk 0.6, fixed 0. Only a triage decision or a proved fix
                      lowers it.
                    </p>
                  </div>
                </div>
                <div className="xp-formula">
                  <span>
                    Index = <code>Σ ( Reach × Threat × Residual gap )</code>
                  </span>
                  <span className="muted">over every finding that is not a false positive</span>
                </div>
              </section>
            </>
          ) : tab === 'paths' ? (
            <>
              <section className="card panel xp-cuts" aria-labelledby="xp-cuts">
                <div className="xp-head">
                  <h2 id="xp-cuts" className="panel-title">
                    Break the path, clear the debt
                  </h2>
                  <Link className="btn btn-outline btn-sm" to="/app/surface?view=paths">
                    Open proof paths
                  </Link>
                </div>
                <p className="muted xp-lede">
                  A finding that reaches a secret depends on a chain of hops. Cut one and everything
                  behind it stops mattering. These are the single hops whose cut would pay down the
                  most, estimated from the proof paths shown.
                </p>
                {data.cuts.length === 0 ? (
                  <p className="muted">
                    No open finding reaches data or a secret through a hop that could be cut.
                  </p>
                ) : (
                  <ol className="xp-cut-list">
                    {data.cuts.map((c) => (
                      <li key={c.nodeId} className="xp-cut">
                        <span
                          className={`xp-stripe ${c.reaches.length > 0 ? 'xp-stripe-hot' : ''}`}
                          aria-hidden="true"
                        />
                        <div>
                          <div className="xp-cut-name">
                            Cut <span className="mono">{c.name}</span>{' '}
                            <span className="muted">· {NODE_TYPE[c.type].label}</span>
                          </div>
                          <div className="xp-meta">
                            {c.findings} {c.findings === 1 ? 'finding' : 'findings'} stop reaching{' '}
                            {c.reaches.join(', ')}
                          </div>
                        </div>
                        <span className="xp-val xp-neg">−{fmt(c.debtReduction)}</span>
                        <span className="xp-track xp-cut-track">
                          <i
                            className="xp-fill xp-fill-accent"
                            style={{ width: `${(c.debtReduction / totalCut) * 100}%` }}
                          />
                        </span>
                      </li>
                    ))}
                  </ol>
                )}
              </section>

              <section className="card panel" aria-labelledby="xp-items">
                <h2 id="xp-items" className="panel-title">
                  Largest debt first
                </h2>
                <div className="table-wrap">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Severity</th>
                        <th>Finding</th>
                        <th>Reach</th>
                        <th>Threat</th>
                        <th className="num">Debt</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.items.slice(0, 15).map((i) => (
                        <tr key={i.findingId}>
                          <td>
                            <span className={`sev-${i.severity.toLowerCase()}`}>{i.severity}</span>
                          </td>
                          <td>
                            <Link className="table-link" to={`/app/engagements/${i.engagementId}`}>
                              {i.title}
                            </Link>
                            {i.status === 'ACCEPTED_RISK' ? (
                              <span className="muted"> · accepted risk</span>
                            ) : null}
                          </td>
                          <td>
                            <span className={`reach ${REACH[i.reach].cls}`}>
                              {REACH[i.reach].label}
                            </span>
                          </td>
                          <td>
                            {i.kev ? <span className="xp-chip xp-chip-kev">KEV</span> : null}
                            {i.epss !== null ? (
                              <span className="xp-meta"> EPSS {fmt(i.epss * 100)}%</span>
                            ) : null}
                            {!i.kev && i.epss === null ? <span className="muted">—</span> : null}
                          </td>
                          <td className="num mono">{fmt(i.debt)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            </>
          ) : tab === 'reduction' ? (
            <div className="xp-grid xp-1-1">
              <section className="card panel" aria-labelledby="xp-weak">
                <h2 id="xp-weak" className="xp-eyebrow">
                  Debt by weakness
                </h2>
                {data.byWeakness.length === 0 ? (
                  <p className="muted">No open findings.</p>
                ) : (
                  <ul className="xp-bars">
                    {data.byWeakness.map((w) => (
                      <li key={w.weakness} className="xp-bar">
                        <span>{WEAKNESS[w.weakness]}</span>
                        <span className="xp-meta">
                          {w.findings} {w.findings === 1 ? 'finding' : 'findings'}
                        </span>
                        <span className="xp-val">{fmt(w.debt)}</span>
                        <span className="xp-track">
                          <i
                            className={`xp-fill w-${w.weakness.toLowerCase()}`}
                            style={{ width: `${(w.debt / totalWeakness) * 100}%` }}
                          />
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </section>

              <section className="card panel" aria-labelledby="xp-fixes">
                <h2 id="xp-fixes" className="xp-eyebrow">
                  Fixes — debt they remove
                </h2>
                {data.fixes.length === 0 ? (
                  <p className="muted">
                    No fix pull requests yet. Turn on automatic remediation for an application to
                    have Guardian propose them.
                  </p>
                ) : (
                  <ul className="xp-bars">
                    {data.fixes.map((f) => (
                      <li key={f.remediationId} className="xp-bar">
                        <span>
                          <Link className="table-link" to={`/app/engagements/${f.engagementId}`}>
                            {f.title}
                          </Link>{' '}
                          <span
                            className={`xp-chip ${f.state === 'PROVED' ? 'xp-chip-ok' : 'xp-chip-wait'}`}
                          >
                            {f.state === 'PROVED' ? 'proved fixed' : 'awaiting proof'}
                          </span>
                        </span>
                        <span className="xp-meta">
                          {f.findings} {f.findings === 1 ? 'finding' : 'findings'}
                        </span>
                        <span className="xp-val xp-neg">−{fmt(f.debt)}</span>
                        <span className="xp-track">
                          <i
                            className={`xp-fill ${f.state === 'PROVED' ? 'xp-fill-ok' : 'xp-fill-accent'}`}
                            style={{ width: `${(f.debt / totalFix) * 100}%` }}
                          />
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            </div>
          ) : (
            <ApplicationDebt rows={data.byApplication} total={totalApp} />
          )}
        </div>
      </section>

      <CtemLoop data={data} />
    </div>
  );
}

/** The frame's subtitle, as in the mock-up: the application carrying most debt, and the rest. */
function acrossApplications(rows: Exposure['byApplication']): string {
  const owing = rows.filter((r) => r.debt > 0);
  if (owing.length === 0) return 'No open, proved exposure across your applications.';
  const [first] = [...owing].sort((a, b) => b.debt - a.debt);
  const others = owing.length - 1;
  return `Accumulated reach from open, proved attack paths across ${first!.name}${
    others === 0 ? '' : ` and ${others} other ${others === 1 ? 'application' : 'applications'}`
  }.`;
}

function ApplicationDebt({ rows, total }: { rows: Exposure['byApplication']; total: number }) {
  if (rows.length === 0) {
    return (
      <section className="card panel">
        <p className="muted">No applications carry open findings.</p>
      </section>
    );
  }
  return (
    <section className="card panel" aria-labelledby="xp-apps">
      <h2 id="xp-apps" className="xp-eyebrow">
        Debt by application
      </h2>
      <ul className="xp-bars">
        {[...rows]
          .sort((a, b) => b.debt - a.debt)
          .map((a) => (
            <li key={a.applicationId} className="xp-bar">
              <span>
                <Link className="table-link" to="/app/applications">
                  {a.name}
                </Link>
              </span>
              <span className="xp-meta">
                {a.findings} {a.findings === 1 ? 'finding' : 'findings'}
              </span>
              <span className="xp-val">{fmt(a.debt)}</span>
              <span className="xp-track">
                <i
                  className="xp-fill xp-fill-accent"
                  style={{ width: `${(a.debt / total) * 100}%` }}
                />
              </span>
            </li>
          ))}
      </ul>
    </section>
  );
}

/** The CTEM loop, with a live figure for each stage. The current stage is this page. */
function CtemLoop({ data }: { data: Exposure }) {
  const { loop } = data;
  const stages = [
    {
      n: 1,
      name: 'Scope',
      value: `${loop.scope.applications}`,
      detail: `applications · ${loop.scope.approvedScopes} approved scopes`,
      to: '/app/applications',
    },
    {
      n: 2,
      name: 'Discover',
      value: `${loop.discover.unownedHosts}`,
      detail: `of ${loop.discover.publicHosts} public hosts have no owner`,
      to: '/app/cloud',
      warn: loop.discover.unownedHosts > 0,
    },
    {
      n: 3,
      name: 'Prioritize',
      value: fmt(loop.prioritize.index),
      detail: `debt across ${loop.prioritize.openFindings} open findings`,
      to: '/app/exposure',
      current: true,
    },
    {
      n: 4,
      name: 'Validate',
      value: `${loop.validate.reachSensitive}`,
      detail: `findings proved to reach data or secrets`,
      to: '/app/surface?view=paths',
      danger: loop.validate.reachSensitive > 0,
    },
    {
      n: 5,
      name: 'Mobilize',
      value: `${loop.mobilize.fixesProved}`,
      detail: `fixes proved · ${loop.mobilize.fixesPending} awaiting proof`,
      to: '/app/engagements',
    },
  ];
  return (
    <nav className="xp-loop" aria-label="CTEM loop">
      {stages.map((s) => (
        <Link
          key={s.n}
          to={s.to}
          className={`xp-stage${s.current ? ' is-current' : ''}`}
          aria-current={s.current ? 'page' : undefined}
        >
          <span className="xp-stage-name">
            <span className="xp-stage-n">{s.n}</span>
            {s.name}
          </span>
          <span className={`xp-stage-value${s.danger ? ' is-danger' : s.warn ? ' is-warn' : ''}`}>
            {s.value}
          </span>
          <span className="xp-stage-detail">{s.detail}</span>
        </Link>
      ))}
    </nav>
  );
}

/** Index as a line over bars of new and closed findings, each on its own honest scale. */
function TrendChart({ trend }: { trend: readonly ExposureMonth[] }) {
  const W = 560;
  const H = 190;
  const left = 46;
  const right = 16;
  const top = 18;
  const base = 160;
  const max = Math.max(1, ...trend.map((m) => m.index));
  const ceiling = niceCeiling(max);
  const maxCount = Math.max(1, ...trend.map((m) => m.newFindings + m.closed));
  const step = (W - left - right) / Math.max(1, trend.length);
  const x = (i: number) => left + step * i + step / 2;
  const y = (v: number) => base - (v / ceiling) * (base - top);
  const barH = (n: number) => (n / maxCount) * 44;
  const label = (key: string) =>
    new Date(`${key}-01T00:00:00Z`).toLocaleString(undefined, { month: 'short', timeZone: 'UTC' });
  const points = trend.map((m, i) => `${x(i)},${y(m.index)}`).join(' ');
  const last = trend.length - 1;

  return (
    <svg
      className="xp-chart"
      viewBox={`0 0 ${W} ${H}`}
      width="100%"
      role="img"
      aria-label={`Debt index by month: ${trend.map((m) => `${label(m.month)} ${fmt(m.index)}`).join(', ')}`}
    >
      {[0, 0.5, 1].map((f) => (
        <g key={f}>
          <line
            className="xp-grid-line"
            x1={left}
            x2={W - right}
            y1={y(ceiling * f)}
            y2={y(ceiling * f)}
          />
          <text className="xp-axis" x={4} y={y(ceiling * f) + 4}>
            {fmt(ceiling * f)}
          </text>
        </g>
      ))}
      {trend.map((m, i) => (
        <g key={m.month}>
          {m.newFindings > 0 ? (
            <rect
              className="xp-bar-new"
              x={x(i) - 14}
              width={28}
              y={base - barH(m.newFindings) - barH(m.closed)}
              height={barH(m.newFindings)}
            />
          ) : null}
          {m.closed > 0 ? (
            <rect
              className="xp-bar-closed"
              x={x(i) - 14}
              width={28}
              y={base - barH(m.closed)}
              height={barH(m.closed)}
            />
          ) : null}
          <text className="xp-axis" x={x(i)} y={H - 6} textAnchor="middle">
            {label(m.month)}
          </text>
        </g>
      ))}
      {trend.length > 1 ? (
        <>
          <path
            className="xp-area"
            d={`M${x(0)},${base} L${points.replaceAll(' ', ' L')} L${x(last)},${base} Z`}
          />
          <polyline className="xp-line" points={points} />
        </>
      ) : null}
      {trend.map((m, i) => (
        <circle
          key={m.month}
          className={i === last ? 'xp-point is-last' : 'xp-point'}
          cx={x(i)}
          cy={y(m.index)}
          r={i === last ? 5.5 : 4}
        />
      ))}
      {trend.length > 0 ? (
        <text
          className="xp-axis xp-axis-strong"
          x={x(last)}
          y={y(trend[last]!.index) - 12}
          textAnchor="middle"
        >
          {fmt(trend[last]!.index)}
        </text>
      ) : null}
    </svg>
  );
}

/** A round axis top a little above the largest value: 38 → 40, 1,284 → 1,500. */
function niceCeiling(max: number): number {
  const magnitude = 10 ** Math.floor(Math.log10(max));
  for (const m of [1, 1.5, 2, 2.5, 3, 4, 5, 6, 8, 10]) {
    if (m * magnitude >= max * 1.05) return m * magnitude;
  }
  return 10 * magnitude;
}
